import {
  useSearchQueryFilter,
  useSearchTypeFromRoute,
  useTrackEvents,
} from '@aibees/elephant/hooks';
import { classnames } from '@aibees/shared/utils';
import { Box } from '@mui/material';
import { memo, useMemo, useState } from 'react';
import { FilterSection } from '../../components/filter-section';
import { getFieldsByFilterItems } from '../../components/filter-tags/utils';
import { useFilterSections } from '../../hooks/use-filter-sections';
import { findOpenFilterItems } from '../../utils';
import {
  TextFieldAccordionFilter,
  TextFieldAccordionFilterProps,
} from '../text-field-accordion-filter';
import type { TextFieldGroupFilterProps as Props } from './types';
import { buildSearchParamsDraft } from './utils';

export const TextFieldGroupFilter = memo(({ className, ...filter }: Props) => {
  const selectKeys = useMemo(
    () => filter.items?.map((i) => i.field!).filter(Boolean) ?? [],
    [filter.items]
  );
  const { setParams, params, getParam } = useSearchQueryFilter({
    select: selectKeys,
  });
  const defaultOpen = findOpenFilterItems(filter)?.at(0) ?? undefined;
  const searchType = useSearchTypeFromRoute();

  const { track } = useTrackEvents();
  const [open, setOpen] = useState<string | undefined>(defaultOpen);

  const fields = getFieldsByFilterItems(filter.items!);

  const { mainItems, moreItems } = useFilterSections(filter, filter.sections);

  const handleChangeDebounced = (
    field: string,
    values: string[],
    newValue?: string
  ) => {
    const draft = buildSearchParamsDraft(
      field,
      fields,
      values,
      params,
      filter.metadata?.singleEntry
    );

    setParams(draft);

    track('FILTER', {
      filterName: field,
      filterType: filter.type,
      filterValue: newValue,
      searchType,
    });
  };

  const handleChange: TextFieldAccordionFilterProps['onChange'] = (
    field,
    values,
    newValue
  ) => {
    handleChangeDebounced(field, values, newValue);
  };

  const isAnyOfMoreItemsSet = useMemo(() => {
    return moreItems?.map((i) => getParam(i.field!)).filter(Boolean);
  }, [moreItems]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1}
      className={classnames('TextFieldGroupFilter', className)}
    >
      {mainItems?.map((item) => {
        const { key, open: o, ...props } = item;
        return (
          <TextFieldAccordionFilter
            open={open === item.field}
            onOpen={setOpen}
            onChange={handleChange}
            key={key}
            {...props}
          />
        );
      })}
      {moreItems?.length ? (
        <FilterSection
          title="More Filters"
          open={!!isAnyOfMoreItemsSet?.length}
        >
          {moreItems?.map((item) => {
            const { key, open: o, ...props } = item;
            return (
              <TextFieldAccordionFilter
                open={open === item.field}
                onOpen={setOpen}
                onChange={handleChange}
                key={key}
                {...props}
              />
            );
          })}
        </FilterSection>
      ) : null}
    </Box>
  );
});

TextFieldGroupFilter.displayName = 'TextFieldGroupFilter';
